/**
 * The `<tc-combobox>` vocabulary: its attributes, its part types and the
 * `data-tc-part` names the block stores and the runtime wires. The authoring
 * plugin and the runtime live in different packages, so the one copy lives
 * here — the same reason `counter-settings.ts` does.
 *
 * A combobox is a PART, not a block: it is stored inside the block that uses
 * it, never offered as a tile or to the copilot (`INTERACTIVE_PART_TAGS`).
 *
 * Every block stores the COMPOUND shape (design.md D107): one GrapesJS type
 * per part (`COMBOBOX_PART_TYPES`), the way the dialog and the carousel are
 * built, with its runtime, form association and GrapesJS types in
 * `combobox-compound.ts`, `combobox-form.ts` and
 * `plugins/interactive/combobox-parts.ts` in `@tripcart/page-builder`.
 */
export const COMBOBOX_TYPE = "tc-combobox";

/**
 * The GrapesJS type of a COMPOUND root. Its element is still `<tc-combobox>`
 * (`COMPONENT_TYPE_TAGS`); the type is separate because `tc-combobox` is
 * the Tour Search block's picker type, with that block's traits and drag
 * rules (it claims a root that carries `TOUR_SEARCH_FIELD_ATTR`). GrapesJS
 * drops a `tagName` equal to the type default, so a saved compound root may
 * carry only this type, which is why it is also in `INTERACTIVE_PART_TAGS`.
 */
export const COMPOUND_COMBOBOX_TYPE = "tc-combobox-compound";

/**
 * The two WAI-ARIA APG combobox patterns, as the Tour Search Behavior
 * setting and the runtime's key handling name them. `select` is the
 * select-only combobox: the listbox takes the keys, with type-ahead.
 * `autocomplete` is the editable one: a search box filters the list. Nothing
 * stores it: a `search` part present means the list filters.
 */
export const COMBOBOX_MODES = ["select", "autocomplete"] as const;
export type ComboboxMode = (typeof COMBOBOX_MODES)[number];
export const DEFAULT_COMBOBOX_MODE: ComboboxMode = "select";

/** A stored value this build does not know falls back to `select`. */
export const readComboboxMode = (value: unknown): ComboboxMode =>
  COMBOBOX_MODES.find((mode) => mode === value) ?? DEFAULT_COMBOBOX_MODE;

/**
 * Attributes on the `<tc-combobox>` root.
 *
 * `placeholder` is the picker's "any" words: the trigger's text while nothing
 * is chosen. It is kept on the root so switching Behavior and back loses
 * nothing.
 *
 * `multiple` (boolean) lets the visitor choose several
 * options. It changes what `aria-selected` means — see `COMBOBOX_STATE`.
 *
 * `name` is the form field the root submits under. The
 * root is a form-associated custom element, so it sends one entry per chosen
 * value (`tour=a&tour=b`) with no hidden inputs, and nothing at all while
 * nothing is chosen.
 */
export const COMBOBOX_ATTRS = {
  mode: "mode",
  placeholder: "placeholder",
  multiple: "multiple",
  name: "name",
  popup: "popup",
} as const;

/**
 * Where a compound root's popup opens on a narrow screen
 * (`COMBOBOX_SHEET_QUERY`). `sheet`, the default, is a modal bottom sheet;
 * `anchored` keeps the popup beside the trigger at every width, as the Tour
 * Search pickers do. On a wide screen both are anchored.
 */
export const COMBOBOX_POPUPS = ["sheet", "anchored"] as const;
export type ComboboxPopup = (typeof COMBOBOX_POPUPS)[number];
export const DEFAULT_COMBOBOX_POPUP: ComboboxPopup = "sheet";

/** A stored value this build does not know falls back to `sheet`. */
export const readComboboxPopup = (value: unknown): ComboboxPopup =>
  COMBOBOX_POPUPS.find((popup) => popup === value) ?? DEFAULT_COMBOBOX_POPUP;

/**
 * The `data-tc-part` names the combobox's children carry.
 *
 * `label` names the picker. `text` is the chosen option's words inside the
 * trigger, and `caret` the chevron beside them. `list` is the
 * `role="listbox"` and `option` each choice in it. `status` is the polite
 * live region: it says when a filter matches nothing (AC-69) and how many
 * options are chosen.
 *
 * `trigger` is the button that opens the popup. `content` is the popup, a `<dialog>` the runtime puts in the top layer: an anchored
 * popover beside the trigger, or a modal bottom sheet on a narrow screen
 * (`COMBOBOX_SHEET_QUERY`). `search` is the text box inside it, and it is the
 * element with `role="combobox"`: focus sits there while the visitor moves
 * through the list. `footer` holds the status and `clear`, the button that
 * empties the choice. `chips` sits beside the trigger, never inside it (a
 * `<button>` may not hold buttons); the runtime fills it with one removable
 * chip per chosen value. `header` (holding `title` and `close`) and `done`
 * show only while the popup is a sheet: a sheet covers the trigger, so it
 * needs its own name and its own way out.
 */
export const COMBOBOX_PARTS = {
  label: "label",
  trigger: "trigger",
  text: "text",
  caret: "caret",
  content: "content",
  search: "search",
  list: "list",
  option: "option",
  footer: "footer",
  status: "status",
  clear: "clear",
  chips: "chips",
  header: "header",
  title: "title",
  close: "close",
  done: "done",
} as const;
export type ComboboxPart = (typeof COMBOBOX_PARTS)[keyof typeof COMBOBOX_PARTS];

/**
 * The GrapesJS type of each compound part. Only the root is a custom element:
 * every part renders as the native element that already behaves right
 * (`COMPONENT_TYPE_TAGS` in `component-element.ts`), and the root reads and
 * writes them all. A part holds no state and runs no code of its own.
 */
export const COMBOBOX_PART_TYPES = {
  label: "tc-combobox-label",
  trigger: "tc-combobox-trigger",
  text: "tc-combobox-text",
  caret: "tc-combobox-caret",
  content: "tc-combobox-content",
  search: "tc-combobox-search",
  list: "tc-combobox-list",
  option: "tc-combobox-option",
  footer: "tc-combobox-footer",
  status: "tc-combobox-status",
  clear: "tc-combobox-clear",
  chips: "tc-combobox-chips",
  header: "tc-combobox-header",
  title: "tc-combobox-title",
  close: "tc-combobox-close",
  done: "tc-combobox-done",
} as const satisfies Record<ComboboxPart, string>;

/**
 * An option's attributes. `value` is what the form sends for it. The option's
 * words are its text, which is also what `search` filters on.
 */
export const COMBOBOX_OPTION_ATTRS = {
  value: "data-value",
} as const;

/**
 * State the runtime writes, so the block's CSS styles from attributes alone.
 *
 * `open` is on the root and on `content` while the popup shows. `sheet` is
 * on the root while the popup opens as a bottom sheet, so the block's CSS can
 * show the sheet-only parts. `highlighted`
 * is the option `aria-activedescendant` names. `chosen` is every option the
 * form will send.
 *
 * `aria-selected` follows the APG pattern of each mode. Single: it marks the
 * HIGHLIGHTED option, which is what a screen reader announces while the
 * visitor moves. `multiple`: it marks every CHOSEN option, as a
 * multi-select listbox requires, and the highlight is `highlighted` alone.
 */
export const COMBOBOX_STATE = {
  open: "data-open",
  sheet: "data-sheet",
  /** On a compound popup last opened under its field, not as a sheet: the
   *  one that may fade out as it closes (`combobox-sheet.ts`). */
  popped: "data-popped",
  highlighted: "data-highlighted",
  chosen: "data-chosen",
} as const;

/**
 * The status words, stored as attributes on the `status` part so the
 * operator can edit them. `{count}` and `{total}` are filled in by the
 * runtime. `none` shows while nothing is chosen, `count` once something is,
 * and `empty` when a filter matches nothing.
 */
export const COMBOBOX_STATUS_ATTRS = {
  none: "data-none",
  count: "data-count",
  empty: "data-empty",
} as const;

/**
 * The `done` button's words, as attributes on it, filled like the status's.
 * `one` is the singular, so a choice of one never reads "Show 1 tours".
 */
export const COMBOBOX_DONE_ATTRS = {
  none: "data-none",
  one: "data-one",
  count: "data-count",
} as const;

/**
 * Attributes on the `chips` part. `max` chips show (default 2), then `more`
 * says how many were left out. `remove` is each chip button's accessible
 * name; `{label}` is the option's words.
 */
export const COMBOBOX_CHIPS_ATTRS = {
  max: "data-max",
  more: "data-more",
  remove: "data-remove",
} as const;

/**
 * The hooks on what the runtime writes into `chips`, for the block's CSS:
 * each chip, its remove button (which carries the value it removes) and the
 * "+N more" note.
 */
export const COMBOBOX_CHIP_HOOKS = {
  chip: "data-chip",
  remove: "data-chip-remove",
  more: "data-chip-more",
} as const;

/** Below this width the popup opens as a modal bottom sheet. */
export const COMBOBOX_SHEET_QUERY = "(max-width: 40rem)";
